import { Ruby } from "./Ruby";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw, Repeat } from "lucide-react";
import { asset } from "../../lib/api";
import { play, pauseAudio, resumeAudio, stopAudio, sfx } from "../../lib/audio";

const spriteAnim = (a, base) => {
  if (!a) return { initial: {}, animate: {} };
  const t = { duration: a.duration || 2, delay: a.delay || 0 };
  switch (a.action) {
    case "walk":
    case "move":
      return { initial: { left: `${a.from_x ?? base - 20}%` }, animate: { left: `${a.to_x ?? base}%`, y: [0, -8, 0, -8, 0] }, transition: { ...t, ease: "easeOut" } };
    case "bounce":
      return { initial: { y: 0 }, animate: { y: [0, -24, 0] }, transition: { ...t, duration: 0.9, repeat: Infinity, repeatDelay: 1.2 } };
    case "fade":
      return { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: t };
    case "scale":
      return { initial: { scale: 0.4 }, animate: { scale: 1 }, transition: { ...t, type: "spring" } };
    default:
      return { initial: {}, animate: {} };
  }
};

const Sprite = ({ c, action }) => {
  const a = spriteAnim(action, c.x);
  return (
    <motion.div className="absolute -translate-x-1/2 -translate-y-1/2 select-none drop-shadow-lg" style={{ left: `${c.x}%`, top: `${c.y}%`, fontSize: `min(${c.size || 9}vw, 15vh)` }}
      initial={a.initial} animate={a.animate} transition={a.transition}>
      {c.image_url ? <img src={asset(c.image_url)} alt="" style={{ height: `min(${(c.size || 9) * 1.4}vw, 20vh)` }} /> : c.emoji}
    </motion.div>
  );
};

const SceneObject = ({ o }) => {
  const [hop, setHop] = useState(0);
  return (
    <motion.button data-testid={`scene-object-${o.id}`} onClick={() => { setHop((h) => h + 1); play(o.sound_text || o.label); }}
      key={hop} animate={hop ? { y: [0, -30, 0], rotate: [0, -10, 10, 0] } : {}} transition={{ duration: 0.6 }}
      className="absolute -translate-x-1/2 -translate-y-1/2 drop-shadow-md" style={{ left: `${o.x}%`, top: `${o.y}%`, fontSize: "min(6vw, 10vh)" }}>
      {o.image_url ? <img src={asset(o.image_url)} alt={o.label} style={{ height: "min(8vw,12vh)" }} /> : o.emoji}
    </motion.button>
  );
};

export const storyIntro = (title) => (title ? (/(故事|冒險)$/.test(title) ? title : `${title}的故事`) : "");

export const StoryPlayer = ({ scenes, autoplay = true, onDone, onReplay, continuous = false, title }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [auto, setAuto] = useState(continuous);
  const [intro, setIntro] = useState(false);
  const introDone = useRef(!title);
  const autoRef = useRef(auto);
  autoRef.current = auto;
  const s = scenes[i];

  const token = useRef(0);
  const narrate = async (idx) => {
    setPaused(false);
    const my = ++token.current;
    const sc = scenes[idx];
    const t0 = Date.now();
    if (idx === 0 && !introDone.current) {
      setIntro(true);
      await play(storyIntro(title));
      if (my !== token.current) return;
      setIntro(false);
      introDone.current = true;
      await new Promise((r) => setTimeout(r, 500));
      if (my !== token.current) return;
    }
    await play(sc.narration_text, sc.narration_audio);
    if (continuous && my === token.current && sc.dialogue_text?.text && autoRef.current) await play(sc.dialogue_text.text, sc.dialogue_audio);
    if (continuous) await new Promise((r) => setTimeout(r, Math.max(1200, 4000 - (Date.now() - t0))));
    if (!autoRef.current || my !== token.current) return;
    if (idx < scenes.length - 1) setI(idx + 1);
    else if (continuous) { stopAudio(); onDone(); }
  };

  useEffect(() => {
    if (autoplay) narrate(i);
    return () => { token.current++; stopAudio(); setIntro(false); };
  }, [i]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return null;
  const cam = s.animation_config?.camera || {};
  const actions = Object.fromEntries((s.animation_config?.actions || []).map((a) => [a.target, a]));
  const next = () => {
    sfx("tap");
    if (i < scenes.length - 1) setI(i + 1);
    else { stopAudio(); onDone(); }
  };
  const togglePause = () => { if (paused) resumeAudio(); else pauseAudio(); setPaused(!paused); };

  return (
    <div className="flex flex-col h-full gap-3" data-testid="story-player">
      <div className="relative flex-1 min-h-[40vh] rounded-[2rem] overflow-hidden border-4 border-amber-200 shadow-xl bg-amber-50" onClick={(e) => e.target === e.currentTarget && next()}>
        <AnimatePresence mode="wait">
          <motion.div key={s.scene_id} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <motion.img src={asset(s.background_asset)} alt="" className="absolute inset-0 w-full h-full object-cover"
              initial={{ scale: 1, x: "0%" }} animate={{ scale: Math.max(cam.zoom || 1, 1.02 + Math.abs(cam.pan_x || 0) / 50), x: `${cam.pan_x || 0}%` }} transition={{ duration: 8, ease: "easeInOut" }} />
            {(s.character_assets || []).map((c) => <Sprite key={c.id} c={c} action={actions[c.id]} />)}
            {(s.interactive_objects || []).map((o) => <SceneObject key={o.id} o={o} />)}
            {s.dialogue_text?.text && (
              <motion.button data-testid="story-dialogue-bubble" onClick={() => play(s.dialogue_text.text, s.dialogue_audio)}
                initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.2, type: "spring" }}
                className="absolute top-5 left-1/2 -translate-x-1/2 bg-white/95 rounded-3xl px-6 py-3 shadow-lg border-2 border-amber-200 max-w-[80%]">
                <span className="text-sm text-[var(--vermilion)] font-bold mr-2">{s.dialogue_text.speaker}</span>
                <span className="font-serif-tc text-xl sm:text-2xl font-bold">{s.dialogue_ruby?.length ? <Ruby data={s.dialogue_ruby} /> : s.dialogue_text.text}</span>
              </motion.button>
            )}
          </motion.div>
        </AnimatePresence>
        <AnimatePresence>
          {intro && i === 0 && title && (
            <motion.div key="intro" data-testid="story-title-card" className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 pointer-events-none"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div initial={{ scale: 0.7 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="scroll-card px-10 py-6 text-center">
                <div className="font-serif-tc font-black text-4xl sm:text-6xl text-[var(--vermilion)]">{title}</div>
                {storyIntro(title) !== title && <div className="font-serif-tc text-xl text-[var(--tea)] mt-2">的故事</div>}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="absolute top-4 right-4 flex gap-1" data-testid="story-scene-dots">
          {scenes.map((_, k) => <span key={k} className={`h-3 rounded-full transition-all shadow ring-1 ring-black/20 ${k === i ? "w-8 bg-[var(--vermilion)]" : "w-3 bg-white"}`} />)}
        </div>
      </div>
      <div className="scroll-card px-3 sm:px-5 py-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
        <p className="flex-1 font-serif-tc text-xl sm:text-2xl leading-relaxed font-bold text-[var(--ink)]" data-testid="story-narration-text">{s.ruby?.length ? <Ruby data={s.ruby} className="text-2xl sm:text-3xl" /> : s.narration_text}</p>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="story-prev-btn" disabled={i === 0} onClick={() => setI(i - 1)} className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white border-2 border-[var(--line)] flex items-center justify-center disabled:opacity-30"><ChevronLeft size={28} /></button>
          <button data-testid="story-replay-btn" onClick={() => { onReplay && onReplay(); narrate(i); }} className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-amber-100 text-[var(--tea)] flex items-center justify-center"><RotateCcw size={24} /></button>
          <button data-testid="story-pause-btn" onClick={togglePause} className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-amber-100 text-[var(--tea)] flex items-center justify-center">{paused ? <Play size={24} /> : <Pause size={24} />}</button>
          <button data-testid="story-auto-toggle" onClick={() => { const on = !auto; setAuto(on); autoRef.current = on; if (on) narrate(i); }} title="Auto play" className={`w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center ${auto ? "bg-[var(--jade)] text-white" : "bg-white border-2 border-[var(--line)] text-stone-500"}`}><Repeat size={22} /></button>
          <motion.button data-testid="story-next-btn" onClick={next} animate={{ scale: [1, 1.06, 1] }} transition={{ repeat: Infinity, duration: 1.6 }}
            className="kid-btn px-5 sm:px-7 bg-[var(--vermilion)] text-white text-xl font-serif-tc flex items-center gap-1 whitespace-nowrap">
            {i < scenes.length - 1 ? "繼續" : "完成"} <ChevronRight />
          </motion.button>
        </div>
      </div>
    </div>
  );
};
